/*
  elovia garms — theme behaviour.

  Three independent pieces: the cart drawer, the product gallery, and the size
  selector. Everything is bound with event delegation on `document`, so the cart
  markup can be swapped wholesale after a cart change without rebinding.

  Cart updates use Shopify's Cart AJAX API with the `sections` parameter, which
  returns freshly rendered section markup in the same response — so the drawer
  is re-rendered by Liquid rather than rebuilt in JavaScript.
*/
(function () {
  'use strict';

  var DRAWER_SECTION = 'cart-drawer';
  var CLOSE_MS = 400;

  /* ------------------------------------------------------------- helpers --- */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function money(cents) {
    // Only used as a fallback; Liquid formats prices everywhere it can.
    return '$' + (cents / 100).toFixed(2);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          throw new Error(data.description || data.message || 'Cart error');
        }
        return data;
      });
    });
  }

  /* ---------------------------------------------------------- cart drawer --- */

  var closeTimer = null;

  function drawerParts() {
    var root = $('[data-cart-drawer-root]');
    if (!root) return null;
    return { root: root, panel: $('.cart', root), scrim: $('.cart__scrim', root) };
  }

  function openDrawer() {
    var p = drawerParts();
    if (!p) return;
    clearTimeout(closeTimer);
    p.scrim.hidden = false;
    p.panel.style.visibility = 'visible';
    p.panel.setAttribute('aria-hidden', 'false');
    // Next frame, so the transition has a starting state to animate from.
    requestAnimationFrame(function () {
      p.panel.setAttribute('data-open', 'true');
      p.scrim.setAttribute('data-open', 'true');
    });
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    var p = drawerParts();
    if (!p) return;
    p.panel.setAttribute('data-open', 'false');
    p.scrim.setAttribute('data-open', 'false');
    p.panel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    closeTimer = setTimeout(function () {
      var q = drawerParts();
      if (!q) return;
      q.scrim.hidden = true;
      q.panel.style.visibility = 'hidden';
    }, CLOSE_MS);
  }

  function setCount(n) {
    var badge = $('[data-cart-count]');
    if (!badge) return;
    badge.textContent = n;
    badge.hidden = !n;
  }

  /** Swap in freshly rendered drawer markup, keeping it open. */
  function applySections(data) {
    if (typeof data.item_count === 'number') setCount(data.item_count);

    var html = data.sections && data.sections[DRAWER_SECTION];
    if (!html) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-cart-drawer-root]');
    var current = $('[data-cart-drawer-root]');
    if (!fresh || !current) return;

    var wasOpen = current.querySelector('.cart').getAttribute('data-open') === 'true';
    current.replaceWith(fresh);
    if (wasOpen) openDrawer();
  }

  function changeLine(index, quantity) {
    return postJSON(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart/change.js'
      : '/cart/change.js', {
      line: index,
      quantity: quantity,
      sections: DRAWER_SECTION,
      sections_url: window.location.pathname
    })
      .then(applySections)
      .catch(function (err) {
        console.error(err);
        window.location.href = '/cart';
      });
  }

  /* ------------------------------------------------------ add to cart form --- */

  function bindAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      var input = form.querySelector('[data-variant-input]');
      if (!input || !input.value) return;

      e.preventDefault();

      var button = form.querySelector('[data-add]');
      var label = form.querySelector('[data-add-label]');
      var original = label ? label.textContent : '';
      if (button) button.disabled = true;

      postJSON('/cart/add.js', {
        items: [{ id: Number(input.value), quantity: 1 }],
        sections: DRAWER_SECTION,
        sections_url: window.location.pathname
      })
        .then(function (data) {
          applySections(data);
          openDrawer();
        })
        .catch(function (err) {
          if (label) label.textContent = err.message;
          setTimeout(function () {
            if (label) label.textContent = original;
          }, 2200);
        })
        .then(function () {
          if (button) button.disabled = false;
        });
    });
  }

  /* --------------------------------------------------------------- gallery --- */

  function bindGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;

    var stage = $('[data-gallery-stage]', gallery);
    var frames = stage ? Array.prototype.slice.call(stage.querySelectorAll('img')) : [];
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (frames.length < 2) return;

    var index = 0;

    function show(next) {
      index = (next + frames.length) % frames.length;
      frames.forEach(function (img, i) {
        img.setAttribute('data-active', i === index ? 'true' : 'false');
        if (i === index) {
          img.removeAttribute('aria-hidden');
        } else {
          img.setAttribute('aria-hidden', 'true');
        }
      });
      thumbs.forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    var prev = $('[data-gallery-prev]', gallery);
    var next = $('[data-gallery-next]', gallery);
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    thumbs.forEach(function (btn, i) {
      btn.addEventListener('click', function () { show(i); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });

    // Swipe: the arrows are intentionally small, so the stage takes gestures.
    var start = null;
    var SWIPE = 40;
    stage.addEventListener('touchstart', function (e) {
      var t = e.touches[0];
      start = { x: t.clientX, y: t.clientY };
    }, { passive: true });

    stage.addEventListener('touchend', function (e) {
      if (!start) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - start.x;
      var dy = t.clientY - start.y;
      start = null;
      // Ignore mostly-vertical drags so the page still scrolls.
      if (Math.abs(dx) < SWIPE || Math.abs(dx) < Math.abs(dy)) return;
      show(dx < 0 ? index + 1 : index - 1);
    }, { passive: true });
  }

  /* -------------------------------------------------------- size selector --- */

  function bindSizes() {
    var product = $('[data-product]');
    if (!product) return;

    var cells = Array.prototype.slice.call(product.querySelectorAll('.size[data-variant]'));
    var input = $('[data-variant-input]', product);
    var price = $('[data-product-price]', product);
    var addButton = $('[data-add]', product);
    var addLabel = $('[data-add-label]', product);
    if (!cells.length || !input) return;

    cells.forEach(function (cell) {
      cell.addEventListener('click', function () {
        if (cell.disabled) return;
        cells.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
        cell.setAttribute('aria-pressed', 'true');
        input.value = cell.getAttribute('data-variant');
        if (price && cell.getAttribute('data-price')) {
          price.textContent = cell.getAttribute('data-price');
        }
        if (addButton) addButton.disabled = false;
        if (addLabel) addLabel.textContent = addLabel.getAttribute('data-add-text') || 'Add to cart';
      });
    });
  }

  /* ------------------------------------------------------- password reveal --- */

  function bindPassword() {
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-password-toggle]');
      if (!toggle) return;
      var panel = $('[data-password-panel]');
      if (!panel) return;
      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        var field = panel.querySelector('input');
        if (field) field.focus();
      }
    });
  }

  /* ------------------------------------------------------------- delegation --- */

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    var qty = e.target.closest('[data-qty]');
    if (qty) {
      e.preventDefault();
      changeLine(Number(qty.getAttribute('data-line-index')), Number(qty.getAttribute('data-qty')));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  function init() {
    bindAddToCart();
    bindGallery();
    bindSizes();
    bindPassword();
    // Keep the label available for restoring after a variant change.
    var addLabel = $('[data-add-label]');
    if (addLabel && !addLabel.getAttribute('data-add-text')) {
      addLabel.setAttribute('data-add-text', addLabel.textContent.trim());
    }
    void money;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
